import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { Button, Input, SkipperAvatar } from '@skipper/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { CourseRibbonSVG } from '@components/common/svgs/CourseRibbonBackgroundSVG';
import { faChevronDown, faCheck } from '@fortawesome/pro-solid-svg-icons';

export const SelectPeopleDropdown = ({
    familyData = [],
    childUsers = [],
    adultUsers = [],
    selectedUsers = [],
    selectedSet = [],
    setSelectedSet,
    setSelectedUsers,
    courseMemberGroupTypes = [],
    selectedCourses = [],
    setSelectedCourses,
}: {
    familyData: any;
    childUsers: any;
    adultUsers: any;
    selectedUsers: any[];
    selectedSet: any[];
    setSelectedSet: React.Dispatch<React.SetStateAction<any[]>>;
    setSelectedUsers: React.Dispatch<React.SetStateAction<any[]>>;
    courseMemberGroupTypes: any;
    selectedCourses: any[];
    setSelectedCourses: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
    const [open, setOpen] = useState(false);

    const selectPeopleRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(selectPeopleRef, () => {
        setOpen(false);
    });

    const [searchTerm, setSearchTerm] = useState('');

    const usersDropdownOptions = useMemo(() => {
        let tempObject = {
            family: familyData ? [...familyData] : [],
            children: childUsers ? [...childUsers] : [],
            adults: adultUsers ? [...adultUsers] : [],
        };
        if (!!searchTerm) {
            tempObject['family'] = tempObject.family.filter((user: any) => {
                return user.fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            tempObject['children'] = tempObject.children.filter((user: any) => {
                return user.fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            tempObject['adults'] = tempObject.adults.filter((user: any) => {
                return user.fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        return tempObject;
    }, [familyData, childUsers, adultUsers, searchTerm]);

    const coursesDropdownOptions = useMemo(() => {
        if (!courseMemberGroupTypes) return [];
        let tempArray = [...courseMemberGroupTypes];

        if (!!searchTerm) {
            tempArray = tempArray.filter((course: any) => {
                return course.course.title.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        return tempArray;
    }, [courseMemberGroupTypes, searchTerm]);

    return (
        <>
            <SelectedReceipients selectedSet={selectedSet} setSelectedSet={setSelectedSet} />
            <div className="flex items-center mt-5">
                <p className="text-teal-600 font-bold">TO</p>
                <Input
                    type="text"
                    id="chatSelectPeople"
                    height="medium"
                    borderless
                    placeholder="Start typing a name to add a person"
                    onClick={() => setOpen(!open)}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                    }}
                />
            </div>
            {open && (
                <div
                    className="w-[300px] border rounded max-h-[300px] overflow-y-auto absolute px-2 bg-white z-1000 py-3"
                    ref={selectPeopleRef}>
                    {!usersDropdownOptions?.family?.length &&
                        !usersDropdownOptions?.children?.length &&
                        !usersDropdownOptions?.adults?.length &&
                        !coursesDropdownOptions?.length && (
                            <div className="items-center mt-1">
                                <p className="text-gray-600 font-medium mx-auto">
                                    You can&apos;t create a chat with anyone
                                </p>
                            </div>
                        )}
                    {!!usersDropdownOptions?.family?.length && (
                        <>
                            <p className="text-gray-600 font-medium uppercase">My family</p>
                            {usersDropdownOptions?.family?.map((user: any) => (
                                <UserInfo
                                    key={user.id}
                                    user={user}
                                    setSelectedUsers={setSelectedUsers}
                                    setSelectedSet={setSelectedSet}
                                />
                            ))}
                        </>
                    )}
                    {!!usersDropdownOptions?.children?.length && (
                        <>
                            <p className="text-gray-600 font-medium uppercase mt-4">Children</p>
                            {usersDropdownOptions?.children?.map((user: any) => (
                                <UserInfo
                                    key={user.id}
                                    user={user}
                                    setSelectedUsers={setSelectedUsers}
                                    setSelectedSet={setSelectedSet}
                                />
                            ))}
                        </>
                    )}
                    {!!usersDropdownOptions?.adults?.length && (
                        <>
                            <p className="text-gray-600 font-medium uppercase mt-4">Adults</p>
                            {usersDropdownOptions?.adults?.map((user: any) => (
                                <UserInfo
                                    key={user.id}
                                    user={user}
                                    setSelectedUsers={setSelectedUsers}
                                    setSelectedSet={setSelectedSet}
                                />
                            ))}
                        </>
                    )}
                    {!!coursesDropdownOptions?.length && (
                        <>
                            <p className="text-gray-600 font-medium uppercase mt-4">COURSES</p>
                            {coursesDropdownOptions?.map((course: any) => {
                                return (
                                    <div
                                        className="flex items-center mt-2 cursor-pointer"
                                        key={course.course.id}
                                        onClick={() => {
                                            setSelectedSet((prev) => {
                                                if (
                                                    prev.find(
                                                        (el) =>
                                                            el.type === 'course' &&
                                                            el.element.courseId ===
                                                                course.course.id,
                                                    )
                                                )
                                                    return [...prev];
                                                return [
                                                    ...prev,
                                                    {
                                                        type: 'course',
                                                        element: {
                                                            courseId: course.course.id,
                                                            title: course.course.title,
                                                            iconColor: course.course.iconColor,
                                                            displayMemberGroupTypes:
                                                                course.memberGroupTypes,
                                                            selectedMemberGroupTypes:
                                                                course.memberGroupTypes,
                                                        },
                                                    },
                                                ];
                                            });
                                        }}>
                                        <div
                                            className="p-1 rounded-full w-6 h-6"
                                            style={{ backgroundColor: course.course.iconColor }}>
                                            <CourseRibbonSVG
                                                width={16}
                                                height={16}
                                                viewBox="0 0 24 24"
                                            />
                                        </div>
                                        <div className="ml-2">{course.course.title}</div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export const SelectedReceipients = ({
    selectedSet = [],
    setSelectedSet,
}: {
    selectedSet: any[];
    setSelectedSet: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
    return (
        <div className="flex w-full flex-wrap gap-y-1 mt-4">
            {selectedSet?.map((element) => (
                <>
                    {element.type === 'user' ? (
                        <UserCard user={element?.element} setSelectedSet={setSelectedSet} />
                    ) : (
                        <CourseCard
                            key={element?.element?.courseId}
                            course={element?.element}
                            setSelectedSet={setSelectedSet}
                        />
                    )}
                </>
            ))}
        </div>
    );
};

export const UserCard = ({
    user,
    setSelectedSet,
}: {
    user: any;
    setSelectedSet: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
    return (
        <div
            key={user?.id}
            className="flex items-center cursor-pointer ml-2 border rounded-xl p-2 w-auto">
            <SkipperAvatar {...user} imageUrl={user?.imageFileEntry?.url} size="small" />
            <p className="ml-2 mr-2">{user.fullName}</p>
            <FontAwesomeIcon
                icon={faXmark}
                size="sm"
                className="cursor-pointer ml-3"
                onClick={() => {
                    setSelectedSet((prev) =>
                        prev.filter(
                            (el) =>
                                el.type === 'course' ||
                                (el.type === 'user' && el.element.id !== user.id),
                        ),
                    );
                }}
            />
        </div>
    );
};

export const CourseCard = ({
    course,
    setSelectedSet,
}: {
    course: any;
    setSelectedSet: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
    const [open, setOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(() => {
        return course?.selectedMemberGroupTypes?.map((m: any) => m);
    });
    useEffect(() => {
        setSelectedSet((prev) => {
            return prev.map((el) => {
                if (el.type === 'course' && el.element.courseId === course.courseId) {
                    return {
                        type: 'course',
                        element: {
                            ...el.element,
                            selectedMemberGroupTypes: selectedOptions,
                        },
                    };
                }
                return el;
            });
        });
    }, [selectedOptions]);

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(dropdownRef, () => {
        setOpen(false);
    });
    return (
        <div className="relative">
            <div
                key={course?.courseId}
                className="flex items-center border rounded-xl p-1 ml-2 px-3">
                <div style={{ backgroundColor: course?.iconColor }} className="rounded-full p-2">
                    <CourseRibbonSVG width={16} height={16} viewBox="0 0 24 24" />
                </div>
                <div className="flex flex-col">
                    <p className="ml-2 mr-2">{course?.title}</p>
                    <div
                        className="flex w-full items-center cursor-pointer"
                        onClick={() => setOpen(!open)}>
                        <div className="ml-2 mr-2 text-xs text-gray-500 capitalize">
                            {selectedOptions?.length > 0
                                ? selectedOptions?.map((m: any) => m).join(', ')
                                : 'None'}
                        </div>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            size="xs"
                            className="text-gray-500 m-auto"
                        />
                    </div>
                </div>
                <FontAwesomeIcon
                    icon={faXmark}
                    size="sm"
                    className="cursor-pointer ml-3"
                    onClick={() =>
                        setSelectedSet((prev) =>
                            prev.filter(
                                (el) =>
                                    el.type === 'user' ||
                                    (el.type === 'course' &&
                                        el?.element?.courseId !== course?.courseId),
                            ),
                        )
                    }
                />
            </div>
            {open && (
                <div
                    className="w-[200px] border rounded max-h-[300px] overflow-y-auto absolute px-2 bg-white z-1000 py-3 left-0 right-0 mx-auto mt-1"
                    ref={dropdownRef}>
                    <p className="text-gray-600 font-medium uppercase">Include in chat</p>
                    {course.displayMemberGroupTypes.map((m: any) => (
                        <div
                            key={m}
                            className="flex items-center mt-1 cursor-pointer hover:text-teal-600"
                            onClick={() => {
                                setSelectedOptions((prev) => {
                                    if (prev.find((option) => option === m)) {
                                        return prev.filter((option) => option !== m);
                                    }
                                    return [...prev, m];
                                });
                            }}>
                            <div className="w-4 h-5">
                                {selectedOptions.find((option) => option === m) && (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        size="sm"
                                        className="text-teal-600"
                                    />
                                )}
                            </div>
                            <p className="capitalize ml-1 text-base leading-6 font-normal">
                                {m + 's'}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const UserInfo = ({
    user,
    setSelectedUsers,
    setSelectedSet,
}: {
    user: any;
    setSelectedUsers: React.Dispatch<React.SetStateAction<any[]>>;
    setSelectedSet: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
    return (
        <div
            key={user.id}
            className="flex items-center mt-2 hover:text-teal-600 cursor-pointer"
            onClick={() => {
                setSelectedUsers((prev) => {
                    if (prev.find((u) => u.id === user.id)) return [...prev];
                    return [...prev, user];
                });
                setSelectedSet((prev) => {
                    if (prev.find((el) => el.type === 'user' && el.element.id === user.id))
                        return [...prev];
                    return [...prev, { type: 'user', element: user }];
                });
            }}>
            <SkipperAvatar {...user} imageUrl={user?.imageFileEntry?.url} size="medium" />
            <div className="ml-2">{user.fullName}</div>
        </div>
    );
};
